import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { LeadRejectionReason } from "@/lib/db-types";

/** Et avvist lead (RPC list_rejected_leads, VIB-3032). */
export interface RejectedLead {
  id: string;
  name: string;
  organization_number: string | null;
  municipality: string | null;
  website: string | null;
  list_name: string;
  list_owner_name: string | null;
  rejection_reason: LeadRejectionReason | null;
  /** null = avsluttet for godt. */
  revisit_on: string | null;
  rejected_at: string | null;
  rejected_by_name: string | null;
  note: string | null;
}

/**
 * Avviste leads jeg kan vurdere: egne lister, og felles lister for dem som har Leadlister.
 * Databasen avgjør hvem som ser hva, så nøkkelen bærer bare brukeren.
 */
export function useRejectedLeads(options: { enabled?: boolean } = {}) {
  const { activeUser } = useImpersonation();
  return useQuery({
    queryKey: ["rejected-leads", activeUser?.id],
    queryFn: async (): Promise<RejectedLead[]> => {
      const { data, error } = await supabase.rpc("list_rejected_leads", { p_due_only: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as RejectedLead[];
    },
    enabled: !!activeUser?.id && (options.enabled ?? true),
    staleTime: 60 * 1000,
  });
}

export type ReviewAction = "requeue" | "postpone" | "close";

/** Tilbake i køen, utsett til en ny dato, eller avslutt for godt. */
export function useReviewRejectedLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leadId: string; action: ReviewAction; revisitOn?: string | null }) => {
      const { error } = await supabase.rpc("review_rejected_lead", {
        p_lead_id: input.leadId,
        p_action: input.action,
        p_revisit_on: input.revisitOn ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await invalidateFor(qc, "leads");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
